/**
 * API Configuration for Multi-Function App Architecture
 * 
 * This configuration routes API calls to the appropriate function app:
 * - gwc-auth-func: Authentication & user management
 * - gwc-api-func: Standard CRUD operations
 * - gwc-scraper-func: Web scraping with queue processing
 * - gwc-chat-func: AI chat with queue processing
 */

// Detect environment
const hostname = window.location.hostname;
const isAzureStaticApps = hostname.endsWith('azurestaticapps.net');
const isStaging = isAzureStaticApps && hostname.includes('-staging.');

// Note: CRA builds set NODE_ENV=production for both prod and staging deployments.
// So we must detect staging via hostname rather than relying on NODE_ENV.
const isProduction =
  (hostname === 'imagagrant.com' || hostname === 'www.imagagrant.com') ||
  (isAzureStaticApps && !isStaging && hostname.includes('green-desert-06107ed1e'));

const isDev = isAzureStaticApps && !isProduction && !isStaging;

// Production Azure Function URLs
const PRODUCTION_URLS = {
  auth: 'https://imagagrant-prod-westus2-auth-func.azurewebsites.net/api',
  api: 'https://imagagrant-prod-westus2-api-func.azurewebsites.net/api',
  scraper: 'https://imagagrant-prod-westus2-scraper-func.azurewebsites.net/api',
  chat: 'https://imagagrant-prod-westus2-chat-func.azurewebsites.net/api'
};

// Staging Azure Function URLs (staging slots)
const STAGING_URLS = {
  auth: 'https://imagagrant-prod-westus2-auth-func-staging.azurewebsites.net/api',
  api: 'https://imagagrant-prod-westus2-api-func-staging.azurewebsites.net/api',
  scraper: 'https://imagagrant-prod-westus2-scraper-func-staging.azurewebsites.net/api',
  chat: 'https://imagagrant-prod-westus2-chat-func-staging.azurewebsites.net/api'
};

// Dev/Test Azure Function URLs
const DEV_URLS = {
  auth: 'https://gwc-dev-wus2-auth-func-etf6hkfphmguf6fe.westus2-01.azurewebsites.net/api',
  api: 'https://gwc-dev-wus2-api-func-fhaaagbncthsfsej.westus2-01.azurewebsites.net/api',
  scraper: 'https://gwc-dev-wus2-scraper-func-eyb7fpbcekdhcgga.westus2-01.azurewebsites.net/api',
  chat: 'https://gwc-dev-wus2-chat-func-bvhehnabdub3gmdy.westus2-01.azurewebsites.net/api'
};

// Local development URLs
const LOCAL_URLS = {
  auth: 'http://localhost:7072/api',
  api: 'http://localhost:7071/api',
  scraper: 'http://localhost:7074/api',
  chat: 'http://localhost:7073/api'
};

const API_ENDPOINTS = {
  // Prefer build-time env vars. Otherwise choose based on hostname.
  auth: process.env.REACT_APP_AUTH_API_URL || (isStaging ? STAGING_URLS.auth : isProduction ? PRODUCTION_URLS.auth : isDev ? DEV_URLS.auth : LOCAL_URLS.auth),
  api: process.env.REACT_APP_API_URL || (isStaging ? STAGING_URLS.api : isProduction ? PRODUCTION_URLS.api : isDev ? DEV_URLS.api : LOCAL_URLS.api),
  scraper: process.env.REACT_APP_SCRAPER_API_URL || (isStaging ? STAGING_URLS.scraper : isProduction ? PRODUCTION_URLS.scraper : isDev ? DEV_URLS.scraper : LOCAL_URLS.scraper),
  chat: process.env.REACT_APP_CHAT_API_URL || (isStaging ? STAGING_URLS.chat : isProduction ? PRODUCTION_URLS.chat : isDev ? DEV_URLS.chat : LOCAL_URLS.chat)
};

// Log configuration for debugging
console.log('[API Config] Environment:', isStaging ? 'Staging' : isProduction ? 'Production' : isDev ? 'Development' : 'Local');
console.log('[API Config] Endpoints:', API_ENDPOINTS);

/**
 * Helper function to poll for async job status
 * @param {string} statusUrl - The status URL to poll
 * @param {function} onProgress - Callback for progress updates
 * @param {number} maxAttempts - Maximum number of poll attempts (default: 60)
 * @param {number} interval - Polling interval in ms (default: 2000)
 * @param {string} accessToken - Optional JWT access token for authenticated endpoints
 * @returns {Promise} - Resolves with result when complete
 */
export async function pollJobStatus(statusUrl, onProgress = null, maxAttempts = 60, interval = 2000, accessToken = null) {
  let lastStatus = null;
  for (let i = 0; i < maxAttempts; i++) {
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const response = await fetch(statusUrl, { headers });
    
    if (!response.ok) {
      throw new Error(`Polling failed: ${response.statusText}`);
    }
    
    const status = await response.json();
    lastStatus = status;
    
    // Notify progress callback if provided
    if (onProgress) {
      onProgress(status);
    }
    
    // Check if job is complete
    if (status.status === 'completed') {
      return status;
    }
    
    // Check if job failed
    if (status.status === 'failed') {
      throw new Error(status.statusMessage || status.message || 'Job failed');
    }
    
    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, interval));
  }
  
  const timeoutSeconds = Math.round((maxAttempts * interval) / 1000);
  const lastStatusText = lastStatus?.status || 'unknown';
  const lastMessage = lastStatus?.message || lastStatus?.statusMessage || 'No status message';
  throw new Error(`Job timed out after ${timeoutSeconds}s (last status: ${lastStatusText}. ${lastMessage})`);
}

/**
 * Get the appropriate API endpoint for a given function category
 * @param {string} category - The API category (auth, api, scraper, chat)
 * @returns {string} - The base URL for that category
 */
export function getApiEndpoint(category) {
  if (!API_ENDPOINTS[category]) {
    throw new Error(`Unknown API category: ${category}`);
  }
  return API_ENDPOINTS[category];
}

export default API_ENDPOINTS;
